import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, onSnapshot, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { Bell, MessageSquare, AtSign, ArrowLeft, CheckCheck, UserPlus, Users, Heart } from 'lucide-react';

const NotificationsPage = ({ navigate }) => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentUser) return;
        const notifCol = collection(db, 'users', currentUser.uid, 'notifications');

        const unsubscribe = onSnapshot(notifCol, (snapshot) => {
            const notifs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            // Client-side sort to guarantee instant order without indexing requirements
            notifs.sort((a, b) => {
                const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                return timeB - timeA;
            });
            setNotifications(notifs);
            setLoading(false);
        }, (err) => {
            console.error("Notifs error:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const handleNotificationClick = async (notif) => {
        if (!notif.read) {
            try {
                await updateDoc(doc(db, 'users', currentUser.uid, 'notifications', notif.id), {
                    read: true
                });
            } catch (e) {
                console.error("Mark read error:", e);
            }
        }

        if (notif.type === 'friend_request') {
            navigate('friends');
        } else if (notif.type === 'friend_accept') {
            navigate('profile', { userId: notif.fromUserId });
        } else if (notif.postId) {
            navigate('post', { postId: notif.postId });
        } else if (notif.chatId) {
            navigate('chat', { chatId: notif.chatId });
        } else if (notif.fromUserId) {
            navigate('profile', { userId: notif.fromUserId });
        }
    };

    const handleMarkAllAsRead = async () => {
        const unread = notifications.filter(n => !n.read);
        if (unread.length === 0) return;

        try {
            const batch = writeBatch(db);
            unread.forEach(n => {
                const ref = doc(db, 'users', currentUser.uid, 'notifications', n.id);
                batch.update(ref, { read: true });
            });
            await batch.commit();
        } catch (e) {
            console.error("Mark all read error:", e);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'friend_request':
                return <UserPlus className="w-4 h-4 text-emerald-500" />;
            case 'friend_accept':
                return <Users className="w-4 h-4 text-sky-500" />;
            case 'reaction':
                return <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />;
            case 'mention':
                return <AtSign className="w-4 h-4 text-amber-500" />;
            case 'comment':
            default:
                return <MessageSquare className="w-4 h-4 text-brand-500" />;
        }
    };

    const formatTime = (ts) => {
        if (!ts) return '';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        const diff = Math.floor((Date.now() - date.getTime()) / 1000);
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    return (
        <div className="space-y-4 animate-fade-in pb-16">
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate('home')}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors p-1"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to feed</span>
                </button>

                {notifications.some(n => !n.read) && (
                    <button
                        onClick={handleMarkAllAsRead}
                        className="text-xs font-semibold text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1.5"
                    >
                        <CheckCheck className="w-3.5 h-3.5" />
                        <span>Mark all read</span>
                    </button>
                )}
            </div>

            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                    Activity
                </h2>
                {notifications.length > 0 && (
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                        {notifications.filter(n => !n.read).length} unread
                    </span>
                )}
            </div>

            {loading ? (
                <div className="py-16 text-center space-y-3">
                    <div className="w-8 h-8 mx-auto rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                    <p className="text-xs text-slate-400 dark:text-slate-500">Loading activity...</p>
                </div>
            ) : notifications.length > 0 ? (
                <div className="space-y-2">
                    {notifications.map(n => {
                        const avatarUrl = n.fromUserAvatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${n.fromUserId || 'user'}`;
                        return (
                            <div
                                key={n.id}
                                onClick={() => handleNotificationClick(n)}
                                className={`p-3.5 sm:p-4 rounded-2xl border transition-all cursor-pointer flex items-center gap-3.5 ${
                                    !n.read
                                        ? 'bg-sky-50/60 dark:bg-sky-950/20 border-sky-200/80 dark:border-sky-500/30 shadow-xs'
                                        : 'bg-white dark:bg-[#111827] border-slate-200/80 dark:border-slate-800/80 hover:border-slate-300 dark:hover:border-slate-700'
                                }`}
                            >
                                <div className="relative flex-shrink-0">
                                    <img
                                        src={avatarUrl}
                                        alt=""
                                        className="w-10 h-10 rounded-full object-cover bg-slate-100 dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700"
                                    />
                                    <div className="absolute -bottom-1 -right-1 p-1 rounded-full bg-white dark:bg-[#111827] shadow-xs">
                                        {getIcon(n.type)}
                                    </div>
                                </div>

                                <div className="min-w-0 flex-1">
                                    <p className="text-xs text-slate-900 dark:text-slate-100">
                                        <span className="font-bold text-slate-900 dark:text-slate-100 mr-1">
                                            @{n.fromUserName || 'Someone'}
                                        </span>
                                        {n.type === 'friend_request' && 'sent you a friend request.'}
                                        {n.type === 'friend_accept' && 'accepted your friend request! 🎉'}
                                        {n.type === 'reaction' && 'reacted to your thought.'}
                                        {n.type === 'mention' && `mentioned you in a ${n.context || 'thought'}.`}
                                        {n.type === 'comment' && 'commented on your thought.'}
                                        {!['friend_request', 'friend_accept', 'reaction', 'mention', 'comment'].includes(n.type) && 'interacted with your profile.'}
                                    </p>
                                    {(n.commentText || n.postTextSnippet) && (
                                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5 font-normal">
                                            "{n.commentText || n.postTextSnippet}"
                                        </p>
                                    )}
                                    <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                                        {formatTime(n.createdAt)}
                                    </span>
                                </div>

                                {!n.read && (
                                    <span className="w-2.5 h-2.5 rounded-full bg-sky-500 flex-shrink-0"></span>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-16 bg-white dark:bg-[#111827] border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6 space-y-3">
                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto text-slate-400 dark:text-slate-500">
                        <Bell className="w-6 h-6" />
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-200">
                        All caught up!
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
                        When people send you friend requests, reply to your thoughts, or mention you, you'll see them here.
                    </p>
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;
